// Three hand-authored interactives — one per primitive type.
// These are the reference samples used for CHECKPOINT 1 and as templates
// for the full Maths generation script in Part 2.

export const TEST_INTERACTIVES = [
  // ───────────────────── 1. MANIPULATIVE: fraction bar ─────────────────────
  {
    interactive_id: 'test_fraction_bar_three_quarters',
    type: 'manipulative',
    subtype: 'fraction_bar',
    title: 'Shade three-quarters',
    prompt: 'Tap parts of the bar to shade three-quarters (3/4) of it.',
    config: { denominator: 4 },
    success_condition: { type: 'value_equals', target: 0.75, tolerance: 0.0001 },
    on_correct: "Nice — that's three parts out of four. Exactly 3/4.",
    on_wrong_hints: [
      { condition: 'value < 0.5',  hint: 'You need more than half the bar shaded — try adding more pieces.' },
      { condition: 'value === 1',  hint: 'That\'s the whole bar. You want three out of four parts, not all of them.' },
      { condition: 'value > 0.75', hint: 'A little too much. 3/4 is still less than the whole bar.' },
    ],
    feedback_on_stuck: "Remember: the bar is split into 4 parts, and you want to shade 3 of them.",
    time_estimate_seconds: 40,
    learning_objective: 'Student can identify 3/4 as 3 parts of a 4-part whole.',
  },

  // ───────────────────── 2. SIMULATION: projectile ─────────────────────
  {
    interactive_id: 'test_projectile_max_range',
    type: 'simulation',
    subtype: 'projectile',
    title: 'Find the angle for maximum range',
    prompt: 'Keep velocity and gravity fixed. Adjust the launch angle to find the angle that gives the longest range.',
    parameters: [
      { name: 'angle',    min: 0,  max: 90, default: 20, unit: '°' },
      { name: 'velocity', min: 20, max: 30, default: 25, unit: ' m/s' },
      { name: 'gravity',  min: 9,  max: 10, default: 9.8, unit: ' m/s²' },
    ],
    observables: ['range', 'max_height', 'flight_time'],
    success_condition: { type: 'observable_maximized', parameter: 'angle', optimum: 45, tolerance: 2 },
    feedback_on_correct: 'Exactly — 45° gives the longest range when there is no air resistance.',
    scaffolded_hints: [
      'Try angles on either side of 45° and watch the range value change.',
      'Notice that 30° and 60° give roughly the same range — what does that tell you about the peak?',
      'The peak is right in the middle. Try 45°.',
    ],
    feedback_on_stuck: 'Without air resistance, the maximum range is always at 45°.',
    time_estimate_seconds: 90,
    learning_objective: 'Student discovers that projectile range is maximised at 45° for symmetric launch/landing.',
  },

  // ───────────────────── 3. SOLVER: guided linear equation ─────────────────────
  {
    interactive_id: 'test_solver_3x_plus_7_eq_22',
    type: 'solver',
    subtype: 'guided_solver',
    title: 'Solve 3x + 7 = 22',
    prompt: 'Work through this linear equation step by step. Type the next line each time.',
    problem: '3x + 7 = 22',
    solution_steps: [
      { expression: '3x + 7 = 22', action: 'Start with the equation' },
      { expression: '3x = 15',      action: 'Subtract 7 from both sides' },
      { expression: 'x = 5',        action: 'Divide both sides by 3' },
    ],
    mode: 'guided',
    check_each_step: true,
    scaffolded_hints: [
      'First, get the 3x term alone. What operation undoes + 7?',
      'Now that 3x = 15, how do you get from 3x to just x?',
      'Divide both sides by 3 to isolate x.',
    ],
    feedback_on_correct: 'Perfect — you isolated x cleanly in two steps.',
    feedback_on_stuck: 'Here\'s the approach: subtract 7 from both sides, then divide both sides by 3.',
    time_estimate_seconds: 120,
    learning_objective: 'Student can solve a two-step linear equation by inverse operations.',
  },

  // ───────────────────── 4. HSC — LE CHATELIER (Chemistry) ─────────────────
  {
    interactive_id: 'le_chatelier_p1',
    type: 'hsc',
    subtype: 'le_chatelier',
    title: 'Le Chatelier — Haber Process',
    yearLevel: 'Year 12',
    curriculum: 'NSW HSC Chemistry — Module 5 Equilibrium',
    problem: 'N₂(g) + 3H₂(g) ⇌ 2NH₃(g), ΔH = −92 kJ/mol. Maximise [NH₃].',
    correctSolution: 'Lower temperature (favours exothermic forward direction), raise pressure (side with fewer moles), add reactants (N₂, H₂), remove product (NH₃).',
    time_estimate_seconds: 240,
    learning_objective: 'Student applies Le Chatelier to predict shift direction and achieve a yield goal.',
  },

  // ───────────────────── 5. HSC — PUNNETT SQUARE (Biology) ─────────────────
  {
    interactive_id: 'punnett_bbxbb',
    type: 'hsc',
    subtype: 'punnett',
    title: 'Punnett Square — Bb × Bb',
    yearLevel: 'Year 12',
    curriculum: 'NSW HSC Biology — Module 5 Heredity',
    problem: 'Monohybrid cross Bb × Bb. Predict offspring genotype and phenotype ratios.',
    correctSolution: 'Offspring genotypes: 1 BB : 2 Bb : 1 bb. Phenotype ratio 3:1 dominant:recessive. The 3:1 ratio is a theoretical expectation — small samples deviate significantly.',
    time_estimate_seconds: 180,
    learning_objective: 'Student reads a Punnett square, distinguishes genotype from phenotype, and understands sample-size effects on observed ratios.',
  },

  // ───────────────────── 6. HSC — MONETARY POLICY (Economics) ─────────────
  {
    interactive_id: 'monetary_policy_p1',
    type: 'hsc',
    subtype: 'monetary_policy',
    title: 'RBA Cash Rate Lab',
    yearLevel: 'Year 12',
    curriculum: 'NSW HSC Economics — Monetary Policy',
    problem: 'Inflation is at 4.2%. Steer it back into the RBA target band of 2–3% within 3 years while keeping unemployment under 6%.',
    correctSolution: 'Raise the cash rate to cool demand; inflation responds with a ~6-month lag and unemployment with ~12 months. Over-tightening pushes unemployment above 6%.',
    time_estimate_seconds: 240,
    learning_objective: 'Student reasons about monetary policy transmission lags and trade-offs.',
  },

  // ───────────────────── 7. HSC — PROJECTILE HIT TARGET (Physics) ─────────
  {
    interactive_id: 'projectile_hsc_p1',
    type: 'hsc',
    subtype: 'projectile_hsc',
    title: 'Projectile Motion — Hit the Target',
    yearLevel: 'Year 12',
    curriculum: 'NSW HSC Physics — Module 5 Advanced Mechanics',
    problem: 'Target platform at x = 75 m, y = 18 m (±3 m tolerance). Choose launch angle and velocity to land on it. Parametric equations provided.',
    correctSolution: 'Solve 75 = v·cos(θ)·t and 18 = v·sin(θ)·t − 4.9·t². Many (θ, v) pairs work — for v=28 m/s, θ≈55° lands within tolerance.',
    time_estimate_seconds: 240,
    learning_objective: 'Student solves simultaneous parametric equations to hit a target point.',
  },

  // ───────────────────── 8. INTEGRATION SOLVER — for Lumi tutor TEST 1 ─────
  // Canonical u-substitution problem. The natural trap is that students try
  // u = x (or u = 2x, u = x + 3) — none of which simplify. The correct
  // substitution is u = x² + 3 because its derivative (2x) is the factor
  // sitting in front of the integrand.
  {
    interactive_id: 'integration_solver_p1',
    type: 'solver',
    subtype: 'guided_solver',
    title: 'Integration by Substitution — Problem 1',
    prompt: 'Evaluate ∫ 2x · (x² + 3)⁵ dx using u-substitution. Type each step on a new line.',
    problem: '∫ 2x · (x² + 3)^5 dx',
    solution_steps: [
      { expression: 'integral(2x * (x^2 + 3)^5, x)', action: 'Start with the integral' },
      { expression: 'u = x^2 + 3',                   action: 'Let u = x² + 3 (the inner function whose derivative appears)' },
      { expression: 'du = 2x dx',                     action: 'Differentiate to get du = 2x dx' },
      { expression: 'integral(u^5, u)',               action: 'The 2x dx becomes du, and (x²+3)^5 becomes u^5' },
      { expression: 'u^6/6 + C',                      action: 'Integrate u^5 with respect to u' },
      { expression: '(x^2 + 3)^6 / 6 + C',            action: 'Substitute back u = x² + 3' },
    ],
    mode: 'freeform',  // we won't be able to symbolically validate most steps, so use freeform with the integrator as the "last line" target
    check_each_step: false,
    yearLevel: 'Year 12',
    curriculum: 'NSW HSC Mathematics Advanced — Integration',
    correctSolution: 'Let u = x² + 3, so du = 2x dx. The integral becomes ∫ u⁵ du = u⁶/6 + C = (x²+3)⁶/6 + C.',
    scaffolded_hints: [
      'Look for an inner function whose derivative appears (as a factor) in the integrand.',
      'The 2x out front is the derivative of x² + 3. That\'s your u.',
      'Substitute: u = x² + 3, du = 2x dx.',
    ],
    feedback_on_correct: 'Nice — spotting that 2x is exactly d/dx(x²+3) is the move.',
    feedback_on_stuck: 'Let u = x² + 3. Then du = 2x dx, so the integral becomes ∫ u⁵ du = u⁶/6 + C.',
    time_estimate_seconds: 240,
    learning_objective: 'Student recognises u-substitution as the inverse of the chain rule, specifically when du appears as a factor.',
  },
];
